import React, { useState, useEffect, useContext, useCallback } from 'react';
import Chart from 'react-apexcharts';
import {
  Banknote,
  ShoppingBag,
  Trophy,
  TrendingUp,
  Store,
  MapPin,
  RefreshCw,
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import PageHeader from '../components/layout/PageHeader';
import StatCard from '../components/dashboard/StatCard';
import GlassmorphicCard from '../components/common/GlassmorphicCard';
import Button from '../components/common/Button';
import { fetchData } from '../api/api';
import { formatCurrency } from '../utils/currency';
import { getCurrentDateISO } from '../utils/date';

const DashboardPage = () => {
  const { authToken, selectedShopCode } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);

  const [salesData, setSalesData] = useState([]);
  const [productSalesData, setProductSalesData] = useState([]);
  const [shopDetails, setShopDetails] = useState(null);

  const [loadingSales, setLoadingSales] = useState(true);
  const [loadingProductSales, setLoadingProductSales] = useState(true);
  const [loadingShopDetails, setLoadingShopDetails] = useState(true);

  const [errorSales, setErrorSales] = useState('');
  const [errorProductSales, setErrorProductSales] = useState('');

  const isDark = theme === 'dark';

  const fetchDashboardData = useCallback(async () => {
    if (!authToken || !selectedShopCode) return;

    // Fetch Shop Details
    setLoadingShopDetails(true);
    try {
      const shopData = await fetchData(
        `api/pos/v1/setting/all`,
        'GET',
        null,
        authToken,
        selectedShopCode
      );
      setShopDetails(shopData.data || {});
    } catch (error) {
      setShopDetails(null);
    } finally {
      setLoadingShopDetails(false);
    }

    // Fetch Sales Data
    setLoadingSales(true);
    setErrorSales('');
    try {
      const today = getCurrentDateISO();
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const salesReport = await fetchData(
        `api/pos/v1/report/sales/days?date_start=${sevenDaysAgo}&date_end=${today}`,
        'GET',
        null,
        authToken,
        selectedShopCode
      );
      setSalesData(salesReport.data || []);
    } catch (error) {
      setErrorSales('Failed to load sales data.');
      setSalesData([]);
    } finally {
      setLoadingSales(false);
    }

    // Fetch Product Sales Data
    setLoadingProductSales(true);
    setErrorProductSales('');
    try {
      const today = getCurrentDateISO();
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const productReport = await fetchData(
        `api/pos/v1/report/products/days?date_start=${thirtyDaysAgo}&date_end=${today}`,
        'GET',
        null,
        authToken,
        selectedShopCode
      );
      setProductSalesData(productReport.data || []);
    } catch (error) {
      setErrorProductSales('Failed to load product sales data.');
      setProductSalesData([]);
    } finally {
      setLoadingProductSales(false);
    }
  }, [authToken, selectedShopCode]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Aggregations
  const totalSales = salesData.reduce((sum, item) => sum + (+item.saleValue || 0), 0);
  const totalProfit = salesData.reduce((sum, item) => sum + (+item.pnlValue || 0), 0);
  const totalOrders = salesData.reduce((sum, item) => sum + (+item.count || 0), 0);
  
  const topProduct = productSalesData.length > 0
    ? productSalesData.reduce((max, item) => ((item.quantity || 0) > (max.quantity || 0) ? item : max), productSalesData[0])
    : { name: 'N/A', quantity: 0 };

  // ApexCharts Config for Sales & Profit Trend (Spline Area)
  const salesChartOptions = {
    chart: {
      type: 'area',
      height: 350,
      fontFamily: 'Lato, sans-serif',
      toolbar: { show: false },
      background: 'transparent',
    },
    colors: ['#059669', '#0d9488'],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [0, 90, 100],
      },
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: 'smooth',
      width: 2.5,
    },
    grid: {
      borderColor: isDark ? '#334155' : '#e2e8f0',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: salesData.map(item => item.period || ''),
      labels: {
        style: {
          colors: isDark ? '#94a3b8' : '#64748b',
          fontSize: '11px',
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: {
          colors: isDark ? '#94a3b8' : '#64748b',
          fontSize: '11px',
        },
        formatter: (val) => formatCurrency(val),
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      labels: { colors: isDark ? '#f8fafc' : '#0f172a' },
      markers: { radius: 12 },
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      y: {
        formatter: (val) => formatCurrency(val),
      },
    },
  };

  const salesChartSeries = [
    {
      name: 'Total Sales',
      data: salesData.map(item => +item.saleValue || 0),
    },
    {
      name: 'Gross Profit',
      data: salesData.map(item => +item.pnlValue || 0),
    },
  ];

  // ApexCharts Config for Top 5 Products Bar Chart
  const top5Products = productSalesData.slice(0, 5);
  const topProductsChartOptions = {
    chart: {
      type: 'bar',
      height: 350,
      fontFamily: 'Lato, sans-serif',
      toolbar: { show: false },
      background: 'transparent',
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '45%',
        distributed: true,
      },
    },
    colors: ['#059669', '#0d9488', '#0ea5e9', '#8b5cf6', '#f59e0b'],
    dataLabels: {
      enabled: true,
      style: { fontSize: '11px', fontWeight: 600 },
      offsetY: -20,
    },
    grid: {
      borderColor: isDark ? '#334155' : '#e2e8f0',
      strokeDashArray: 4,
    },
    xaxis: {
      categories: top5Products.map(item => item.name || 'Unknown'),
      labels: {
        style: {
          colors: isDark ? '#94a3b8' : '#64748b',
          fontSize: '11px',
        },
        trim: true,
        maxHeight: 60,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: {
          colors: isDark ? '#94a3b8' : '#64748b',
          fontSize: '11px',
        },
      },
    },
    legend: { show: false },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      y: {
        formatter: (val) => `${val} Units Sold`,
      },
    },
  };

  const topProductsChartSeries = [
    {
      name: 'Quantity Sold',
      data: top5Products.map(item => item.quantity || 0),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Dashboard"
        subtitle="Overview of store performance and sales metrics"
        breadcrumbs={[{ label: 'Dashboard' }]}
        action={
          <Button
            onClick={fetchDashboardData}
            variant="outline"
            size="sm"
            icon={<RefreshCw className="w-3.5 h-3.5" />}
          >
            Refresh Data
          </Button>
        }
      />

      {/* Shop Info Card */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 rounded-2xl p-5 text-white shadow-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 shrink-0">
            <Store className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">
              {loadingShopDetails ? 'Loading...' : shopDetails?.shop_name || 'Store'}
            </h2>
            <div className="flex items-center gap-1.5 text-emerald-100/90 text-xs mt-0.5">
              <MapPin className="w-3.5 h-3.5" />
              <span>
                {shopDetails?.shop_address_1
                  ? `${shopDetails.shop_address_1} ${shopDetails.shop_address_2 || ''}`
                  : 'Store Location'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-black/15 px-3.5 py-1.5 rounded-lg border border-white/10">
          <span className="text-xs font-semibold text-white tracking-wider">{selectedShopCode}</span>
        </div>
      </div>

      {/* KPI Stat Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          title="Total Sales (7 Days)"
          value={formatCurrency(totalSales)}
          icon={Banknote}
          color="emerald"
          loading={loadingSales}
          trend={{ value: '+14.2%', isPositive: true }}
        />
        <StatCard
          title="Gross Profit (7 Days)"
          value={formatCurrency(totalProfit)}
          icon={TrendingUp}
          color="teal"
          loading={loadingSales}
          trend={{ value: '+8.6%', isPositive: true }}
        />
        <StatCard
          title="Total Orders (7 Days)"
          value={totalOrders.toLocaleString()}
          icon={ShoppingBag}
          color="sky"
          loading={loadingSales}
          trend={{ value: '+5.1%', isPositive: true }}
        />
        <StatCard
          title="Top Product (30 Days)"
          value={topProduct.name || 'N/A'}
          subtitle={topProduct.quantity ? `${topProduct.quantity} units sold` : 'Top Seller'}
          icon={Trophy}
          color="amber"
          loading={loadingProductSales}
        />
      </div>

      {/* ApexCharts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales & Profit Chart (2 Cols) */}
        <div className="lg:col-span-2">
          <GlassmorphicCard
            title="Daily Sales & Profit"
            subtitle="Past 7 days"
            noPadding
          >
            <div className="p-4 md:p-6">
              {loadingSales ? (
                <div className="h-[350px] flex items-center justify-center text-slate-400">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    <span>Loading...</span>
                  </div>
                </div>
              ) : errorSales ? (
                <div className="h-[350px] flex items-center justify-center text-rose-500 text-sm">
                  {errorSales}
                </div>
              ) : salesData.length === 0 ? (
                <div className="h-[350px] flex items-center justify-center text-slate-400 text-sm">
                  No sales recorded in the selected period.
                </div>
              ) : (
                <Chart
                  options={salesChartOptions}
                  series={salesChartSeries}
                  type="area"
                  height={350}
                />
              )}
            </div>
          </GlassmorphicCard>
        </div>

        {/* Top 5 Products Bar Chart (1 Col) */}
        <div className="lg:col-span-1">
          <GlassmorphicCard
            title="Top 5 Products"
            subtitle="Past 30 days"
            noPadding
          >
            <div className="p-4 md:p-6">
              {loadingProductSales ? (
                <div className="h-[350px] flex items-center justify-center text-slate-400">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    <span>Loading...</span>
                  </div>
                </div>
              ) : errorProductSales ? (
                <div className="h-[350px] flex items-center justify-center text-rose-500 text-sm">
                  {errorProductSales}
                </div>
              ) : top5Products.length === 0 ? (
                <div className="h-[350px] flex items-center justify-center text-slate-400 text-sm">
                  No product sales data available.
                </div>
              ) : (
                <Chart
                  options={topProductsChartOptions}
                  series={topProductsChartSeries}
                  type="bar"
                  height={350}
                />
              )}
            </div>
          </GlassmorphicCard>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
